import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {Tag} from '@/components/ui/tag'
import {useOperatorPage} from '@/components/no-access-state'
import {PlanLockedPage} from '@/components/plan-upsell'
import {BateriaSolicitudBlock} from '@/components/bateria-solicitud-block'
import {InformeExternoSection} from '@/components/bateria-informe-externo'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useBateriaConsolidado} from '@/hooks/bateria/use-bateria-solicitudes'
import {formatDateOnly} from '@/lib/risk'

const PASOS = ['solicita', 'aplica', 'informe'] as const

/** El paso 2 es la guarda legal visible: la batería se aplica en papel, nunca en línea (Res. 2764/2022). */
function Pasos() {
  const {t} = useTranslation()
  return (
    <ol className="surface-divider grid gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-3">
      {PASOS.map((paso, i) => (
        <li key={paso} className="bg-card p-4" data-testid={`bateria-paso-${paso}`}>
          <span className="font-mono text-[10px] font-semibold tracking-[0.14em] text-primary uppercase">
            {t('bateria.page.pasoN', {n: i + 1})}
          </span>
          <p className="mt-1.5 text-sm font-medium text-foreground">{t(`bateria.page.pasos.${paso}.title`)}</p>
          <p className="mt-1 text-xs leading-snug text-muted-foreground">{t(`bateria.page.pasos.${paso}.body`)}</p>
        </li>
      ))}
    </ol>
  )
}

export function BateriaPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {business, canEdit, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const consolidado = useBateriaConsolidado(business?.id, !!business && capabilities.can_use_bateria)
  if (guard) return guard
  if (!business) return null

  if (!capabilities.can_use_bateria) {
    return <PlanLockedPage feature={t('plans.feature.bateria')} businessId={business.id} businessName={business.name} />
  }

  const con = consolidado.data
  const periodicidadCopy =
    con?.periodicidad_meses === 12
      ? t('bateria.consolidado.anual')
      : con?.periodicidad_meses === 24
        ? t('bateria.consolidado.bienal')
        : con?.origen === 'externo'
          ? t('bateria.informeExterno.sinNivel')
          : t('bateria.consolidado.sinDato')
  const puedeSolicitar = !(con?.origen === 'externo' && !con.reaplicacion_vencida)

  return (
    <div className="space-y-6">
      <PageHeading title={t('bateria.page.title')} subtitle={business.name} />

      <section className="surface surface-raised mx-auto max-w-2xl" data-testid="bateria-page-card">
        <header className="surface-divider border-b px-6 py-4">
          <Tag tone="neutral">{t('bateria.page.badge')}</Tag>
          <h2 className="mt-2.5 font-heading text-lg text-foreground">{t('bateria.page.headline')}</h2>
        </header>

        <div className="space-y-5 p-6">
          <Pasos />
          {/* `no_cumple` habilita el CTA fuera del checklist del estándar 4.1.2. */}
          <BateriaSolicitudBlock
            businessId={business.id}
            standardStatus="no_cumple"
            canEdit={canEdit}
            reaplicacionVencida={!!con?.reaplicacion_vencida}
            puedeSolicitar={puedeSolicitar}
          />
          <InformeExternoSection businessId={business.id} canEdit={canEdit} consolidado={con} />
        </div>

        {/* Re-aplicación según Res. 2764/2022 art. 3: solo nivel global y fecha, nunca cifras por
            dimensión ni datos individuales (custodia art. 5). */}
        {con?.disponible && (
          <div className="surface-divider border-t px-6 py-4" data-testid="bateria-consolidado-periodicidad">
            <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {t('bateria.consolidado.title')}
            </p>
            {con.proxima_aplicacion !== null && (
              <p className="mt-1 text-sm text-foreground" data-testid="bateria-consolidado-proxima">
                {t('bateria.consolidado.proxima', {fecha: formatDateOnly(con.proxima_aplicacion)})}
              </p>
            )}
            {con.proxima_aplicacion !== null ? (
              <PorQue
                label={t('bateria.consolidado.verNorma')}
                testId="bateria-consolidado-norma"
                contenidoTestId="bateria-consolidado-norma-contenido">
                {periodicidadCopy}
              </PorQue>
            ) : (
              <Hint className="mt-1">{periodicidadCopy}</Hint>
            )}
          </div>
        )}

        <p className="surface-divider border-t px-6 py-3 text-xs text-muted-foreground">{t('bateria.page.custodia')}</p>
      </section>
    </div>
  )
}
